import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useWeather, conditionLabels, uvIndexBand } from '@/hooks/useWeather'
import { WeatherIcon } from '@/components/WeatherIcon'
import { useCurrentDeviceLocation } from '@/hooks/useCurrentDeviceLocation'
import { WeatherAttribution } from '@/components/WeatherAttribution'
import { CountdownRing } from '@/components/CountdownRing'
import { formatFlightDuration } from '@/lib/flight-utils'
import { useCrewGetRegionalSettings } from '@/lib/hooks/crew'

dayjs.extend(utc)
dayjs.extend(timezone)

function getCityName(cityString: string | undefined | null) {
  if (!cityString) return ''
  return cityString.split(',')[0]?.trim() ?? cityString
}

interface NextFlightData {
  tripId: string
  tripLegId: string
  departureAirport: string
  departureCity: string
  departureLatitude: string | null
  departureLongitude: string | null
  departureTimezone?: string | null
  departureDateTime: Date
  arrivalAirport: string
  arrivalCity: string
  arrivalLatitude: string | null
  arrivalLongitude: string | null
  arrivalTimezone?: string | null
  arrivalDateTime: Date
  isActive?: boolean
}

type FlightCardVariant = 'glass' | 'tinted' | 'solid' | 'gradient' | 'plain'

interface FlightCardProps {
  nextFlight?: NextFlightData | null
  isLoading?: boolean
  temperatureUnit?: 'Celsius' | 'Fahrenheit'
  variant?: FlightCardVariant
  /**
   * Wide edge-spread layout for the desktop home header: weather pinned to the
   * column edges with the countdown ring centered, header label centered on top
   * and flight time centered below. Defaults to the compact mobile layout.
   */
  fullWidth?: boolean
}

const GLASS_CARD = 'bg-white/10 backdrop-blur-xs border border-white/15'
const TINTED_CARD = 'bg-blue-500/15 backdrop-blur-xs border border-blue-300/20'
const SOLID_CARD =
  'bg-surface-L1 border border-border-L1 shadow-[0px_5px_12.5px_rgba(0,0,0,0.05)]'
const GRADIENT_CARD =
  'border border-transparent shadow-[0px_1px_3.5px_rgba(0,0,0,0.05)] bg-[linear-gradient(163deg,var(--content-brand-bold)_3%,#4d103f_92%)]'
// Frosted glass over the map — blurs the map behind the white text so it stays
// legible (desktop home header).
const PLAIN_CARD = 'bg-black/15 backdrop-blur-md border border-white/15'

const CARD_CLASS: Record<FlightCardVariant, string> = {
  glass: GLASS_CARD,
  tinted: TINTED_CARD,
  solid: SOLID_CARD,
  gradient: GRADIENT_CARD,
  plain: PLAIN_CARD,
}

interface VariantTextTokens {
  primary: string
  subtle: string
  loading: string
}

const TEXT_TOKENS: Record<FlightCardVariant, VariantTextTokens> = {
  glass: {
    primary: 'text-white',
    subtle: 'text-content-brand-subtle',
    loading: 'text-white/60',
  },
  tinted: {
    primary: 'text-white',
    subtle: 'text-content-brand-subtle',
    loading: 'text-white/60',
  },
  plain: {
    primary: 'text-white',
    subtle: 'text-white/80',
    loading: 'text-white/60',
  },
  solid: {
    primary: 'text-content-primary-bold',
    subtle: 'text-content-primary-subtle',
    loading: 'text-content-primary-subtle',
  },
  gradient: {
    primary: 'text-content-primary-white',
    subtle: 'text-content-brand-subtle',
    loading: 'text-white/60',
  },
}

export function FlightCard({
  nextFlight,
  isLoading,
  temperatureUnit = 'Celsius',
  variant = 'glass',
  fullWidth = false,
}: FlightCardProps) {
  const isFahrenheit = temperatureUnit === 'Fahrenheit'
  const toDisplay = (c: number) => (isFahrenheit ? Math.round((c * 9) / 5 + 32) : Math.round(c))
  const unitLabel = isFahrenheit ? '°F' : '°C'
  const cardClass = CARD_CLASS[variant]
  const text = TEXT_TOKENS[variant]

  if (isLoading) {
    return (
      <div className={`rounded-2xl h-[140px] flex items-center justify-center ${cardClass}`} />
    )
  }

  if (nextFlight) {
    return (
      <FlightStateA
        nextFlight={nextFlight}
        toDisplay={toDisplay}
        unitLabel={unitLabel}
        cardClass={cardClass}
        text={text}
        variant={variant}
        fullWidth={fullWidth}
      />
    )
  }

  return <FlightStateB toDisplay={toDisplay} unitLabel={unitLabel} cardClass={cardClass} text={text} />
}

function FlightStateA({
  nextFlight,
  toDisplay,
  unitLabel,
  cardClass,
  text,
  variant,
  fullWidth,
}: {
  nextFlight: NextFlightData
  toDisplay: (c: number) => number
  unitLabel: string
  cardClass: string
  text: VariantTextTokens
  variant: FlightCardVariant
  fullWidth: boolean
}) {
  const { data: depWeather } = useWeather({
    latitude: nextFlight.departureLatitude ? parseFloat(nextFlight.departureLatitude) : undefined,
    longitude: nextFlight.departureLongitude ? parseFloat(nextFlight.departureLongitude) : undefined,
  })
  const { data: arrWeather } = useWeather({
    latitude: nextFlight.arrivalLatitude ? parseFloat(nextFlight.arrivalLatitude) : undefined,
    longitude: nextFlight.arrivalLongitude ? parseFloat(nextFlight.arrivalLongitude) : undefined,
  })

  const { data: regionalSettings } = useCrewGetRegionalSettings()
  // Zulu time == UTC; UTC/Zulu only pick the suffix label (design default: ZU).
  // Local shows each airport's local time with an "LT" suffix instead.
  const tzDisplay = regionalSettings?.timezoneDisplay
  const showLocalTime = tzDisplay === 'Local'
  const timeSuffix = showLocalTime ? 'LT' : tzDisplay === 'UTC' ? 'UTC' : 'ZU'
  const formatFlightTime = (dateTime: string | Date, tz?: string | null) =>
    dayjs.utc(dateTime).tz(showLocalTime ? tz ?? 'UTC' : 'UTC').format('HH:mm')

  const depCondition = depWeather ? conditionLabels[depWeather.weatherCondition] : '—'
  const arrCondition = arrWeather ? conditionLabels[arrWeather.weatherCondition] : '—'
  const depTemp = depWeather ? `${toDisplay(depWeather.temperature)}${unitLabel}` : '—'
  const arrTemp = arrWeather ? `${toDisplay(arrWeather.temperature)}${unitLabel}` : '—'

  const flightDuration = formatFlightDuration(nextFlight.departureDateTime, nextFlight.arrivalDateTime)
  const headerLabel = `${nextFlight.isActive ? 'In flight' : 'Next flight'} · ${dayjs
    .utc(nextFlight.departureDateTime)
    .tz(nextFlight.departureTimezone ?? 'UTC')
    .format('D MMM HH:mm')}`

  const colWidth = 'flex-1 min-w-0'

  const body = (
    <div
      className={`rounded-2xl p-4 flex flex-col gap-3 ${fullWidth ? 'w-full' : 'w-full max-w-[360px] mx-auto'} ${cardClass}`}
    >
      {/* Compact (mobile): label + flight time on one space-between row.
          Full-width (desktop): label centered on top, flight time centered below. */}
      {fullWidth ? (
        <div className={`text-center w-full text-[10px] font-semibold uppercase ${text.subtle} leading-4`}>
          {headerLabel}
        </div>
      ) : (
        <div className={`flex items-center justify-between w-full text-[10px] font-semibold uppercase ${text.subtle} leading-4`}>
          <span>{headerLabel}</span>
          <span>Flight time {flightDuration}</span>
        </div>
      )}
      <div className="flex items-start justify-between gap-x-4 gap-y-2">
        <div className={`flex flex-col items-start ${colWidth}`}>
          <p className={`text-xl font-bold ${text.primary} tracking-[-1px] leading-7 flex items-center gap-1.5 whitespace-nowrap`}>
            {nextFlight.departureAirport}
            {depWeather && <WeatherIcon condition={depWeather.weatherCondition} size={fullWidth ? 36 : 28} className="shrink-0" label={depCondition} />}
          </p>
          <p className={`text-xs font-semibold ${text.subtle} leading-4 truncate w-full`}>{getCityName(nextFlight.departureCity)}</p>
          <p className={`text-base font-semibold ${text.primary} leading-6`}>{depTemp}</p>
          <p className={`text-xs font-semibold ${text.subtle} leading-4 line-clamp-2`}>{depCondition}</p>
          <p className={`text-xs font-semibold ${text.subtle} leading-4 whitespace-nowrap`}>
            {formatFlightTime(nextFlight.departureDateTime, nextFlight.departureTimezone)} {timeSuffix}
          </p>
        </div>

        <CountdownRing
          targetDate={nextFlight.departureDateTime}
          departed={nextFlight.isActive}
          text={text}
          size={fullWidth ? 88 : 72}
        />

        <div className={`flex flex-col items-end ${colWidth}`}>
          <p className={`text-xl font-bold ${text.primary} tracking-[-1px] leading-7 flex items-center gap-1.5 justify-end whitespace-nowrap`}>
            {arrWeather && <WeatherIcon condition={arrWeather.weatherCondition} size={fullWidth ? 36 : 28} className="shrink-0" label={arrCondition} />}
            {nextFlight.arrivalAirport}
          </p>
          <p className={`text-xs font-semibold ${text.subtle} leading-4 truncate w-full text-right`}>{getCityName(nextFlight.arrivalCity)}</p>
          <p className={`text-base font-semibold ${text.primary} leading-6`}>{arrTemp}</p>
          <p className={`text-xs font-semibold ${text.subtle} leading-4 line-clamp-2 text-right`}>{arrCondition}</p>
          <p className={`text-xs font-semibold ${text.subtle} leading-4 whitespace-nowrap text-right`}>
            {formatFlightTime(nextFlight.arrivalDateTime, nextFlight.arrivalTimezone)} {timeSuffix}
          </p>
        </div>
      </div>
      {fullWidth && (
        <div className={`text-center w-full text-[10px] font-semibold uppercase ${text.subtle} leading-4`}>
          Flight time {flightDuration}
        </div>
      )}
      {(depWeather || arrWeather) && (
        <WeatherAttribution className={`${text.subtle} ${fullWidth ? 'mx-auto' : ''}`} />
      )}
    </div>
  )

  if (variant === 'tinted') {
    return <div className="px-4">{body}</div>
  }

  return (
    <Link
      to="/schedule/$tripId"
      params={{ tripId: nextFlight.tripId }}
      search={{ legId: nextFlight.tripLegId }}
    >
      {body}
    </Link>
  )
}

function FlightStateB({
  toDisplay,
  unitLabel,
  cardClass,
  text,
}: {
  toDisplay: (c: number) => number
  unitLabel: string
  cardClass: string
  text: VariantTextTokens
}) {
  const {
    data: location,
    isLoading: isLocLoading,
    error: locError,
    refetch: refetchLocation,
  } = useCurrentDeviceLocation(true)

  const { data: weather, isLoading: isWeatherLoading } = useWeather({
    latitude: location?.latitude,
    longitude: location?.longitude,
    enabled: !!location,
  })

  const condition = weather ? conditionLabels[weather.weatherCondition] : '—'
  const temp = weather ? `${toDisplay(weather.temperature)}${unitLabel}` : '—'
  const feelsLike = weather ? `Feels like ${toDisplay(weather.apparentTemperature)}${unitLabel}` : ''

  // Located, but no weather available and none on the way — e.g. web, where
  // WeatherKit (iOS-only) never runs. Show the place instead of an empty "—".
  const locatedNoWeather = !!location && !weather && !isWeatherLoading

  return (
    <div className="px-4">
      <div className={`rounded-2xl p-4 flex flex-col gap-3 ${cardClass}`}>
        {locError ? (
          <button
            type="button"
            onClick={() => refetchLocation()}
            className="flex flex-col items-center text-center py-4 w-full"
          >
            <p className={`text-xs font-semibold uppercase ${text.subtle}`}>No location</p>
            <p className={`text-base font-bold ${text.primary} mt-1`}>Enable location to see local weather</p>
          </button>
        ) : isLocLoading && !location ? (
          <div className="flex items-center justify-center py-4">
            <span className={`${text.loading} text-sm`}>Finding your location…</span>
          </div>
        ) : locatedNoWeather ? (
          <div className="flex flex-col items-center text-center py-4">
            <p className={`text-xs font-semibold uppercase ${text.subtle}`}>
              {location?.cityName ?? 'Located'}
            </p>
            <p className={`text-base font-bold ${text.primary} mt-1`}>Weather unavailable here</p>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-x-4 w-full">
            {/* Left: local time + city (device clock == local time at the user's
                own location). */}
            <div className="flex flex-col items-start w-[78px]">
              <p className={`text-xs font-semibold ${text.subtle} leading-4`}>Local Time</p>
              <p className={`text-xl font-bold ${text.primary} tracking-[-1px] leading-7`}>
                {dayjs().format('h:mma')}
              </p>
              <p className={`text-xs font-semibold ${text.subtle} leading-4 truncate w-full`}>
                {getCityName(location?.cityName)}
              </p>
            </div>

            {/* Center: condition + temperature. */}
            <div className="flex flex-col items-center">
              {weather && <WeatherIcon condition={weather.weatherCondition} size={64} className="pb-1" label={condition} />}
              <p className={`text-xs font-semibold ${text.subtle} whitespace-nowrap leading-4`}>{condition}</p>
              <p className={`text-[26px] font-bold ${text.primary} leading-7`}>{temp}</p>
              {weather && (
                <p className={`text-xs font-semibold ${text.subtle} whitespace-nowrap leading-4`}>{feelsLike}</p>
              )}
            </div>

            {/* Right: UV index (replaces the design's Air Quality — WeatherKit
                provides no AQI). */}
            <div className="flex flex-col items-end w-[78px] text-right">
              <p className={`text-xs font-semibold ${text.subtle} leading-4`}>UV Index</p>
              <p className={`text-xl font-bold ${text.primary} tracking-[-1px] leading-7`}>
                {weather && weather.uvIndex != null ? Math.round(weather.uvIndex) : '—'}
              </p>
              {weather && weather.uvIndex != null && (
                <p className={`text-xs font-semibold ${text.subtle} leading-4`}>
                  {uvIndexBand(weather.uvIndex)}
                </p>
              )}
            </div>
          </div>
        )}
        {weather && <WeatherAttribution className={text.subtle} />}
      </div>
    </div>
  )
}
